import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableCellsLarge, faBarsStaggered, faHouse } from '@fortawesome/free-solid-svg-icons';
import { formatDate } from '../util/formatDate';

interface Props {
  users: any[];
}

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ymd = (d: Date) => d.toISOString().slice(0, 10);

const HrAbsenceCalendar: React.FC<Props> = ({ users }) => {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [view, setView] = useState<'month' | 'timeline'>('month');
  const [absences, setAbsences] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [holidays, setHolidays] = useState<any[]>([]);
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pinnedDay, setPinnedDay] = useState<string | null>(null);

  // клик вне закреплённого поповера — закрыть
  useEffect(() => {
    if (!pinnedDay) return;
    const close = () => setPinnedDay(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [pinnedDay]);

  const load = useCallback(async () => {
    try {
      const [absRes, typesRes, holRes] = await Promise.allSettled([
        axios.get('hr-absence'),
        axios.get('hr-absence-type'),
        axios.get('hr-public-holiday'),
      ]);
      if (absRes.status === 'fulfilled') setAbsences(Array.isArray(absRes.value.data) ? absRes.value.data : []);
      if (typesRes.status === 'fulfilled') setTypes(Array.isArray(typesRes.value.data) ? typesRes.value.data : []);
      if (holRes.status === 'fulfilled') setHolidays(Array.isArray(holRes.value.data) ? holRes.value.data : []);
    } catch {
      // глобальный interceptor обрабатывает auth-ошибки
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Цвет аватара по человеку (детерминированно по id)
  const PALETTE = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#db2777', '#dc2626', '#0d9488'];
  const userColor = (userId: number) => PALETTE[userId % PALETTE.length];
  const typeName = (typeId: number) => types.find(tp => tp.id === typeId)?.name || '—';
  const typeColor = (typeId: number) => types.find(tp => tp.id === typeId)?.color || 'var(--text-color-calendar)';
  const userRole = (userId: number) => users.find(x => x.id === userId)?.job_title || '';
  const initials = (userId: number) => {
    const u = users.find(x => x.id === userId);
    return u ? `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}` : `#${userId}`;
  };
  const userName = (userId: number) => {
    const u = users.find(x => x.id === userId);
    return u ? `${u.first_name} ${u.last_name}` : `#${userId}`;
  };

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(year, month, 1));
    const lead = (first.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const arr: (string | null)[] = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(ymd(new Date(Date.UTC(year, month, d))));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const filtered = absences.filter(a =>
    (catFilter === 'all' || a.category === catFilter) &&
    (statusFilter === 'all' || a.status === statusFilter) &&
    a.status !== 'cancelled' && a.status !== 'rejected',
  );
  const eventsFor = (iso: string) => filtered.filter(a => iso >= a.startDate && iso <= a.endDate);
  const holidayFor = (iso: string) =>
    holidays.find(h => h.date === iso || (h.isRecurring && String(h.date).slice(5) === iso.slice(5)));

  const monthName = new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  const monthShort = new Date(year, month, 1).toLocaleString('default', { month: 'short' });
  const prev = () => { const m = month - 1; if (m < 0) { setMonth(11); setYear(year - 1); } else setMonth(m); };
  const next = () => { const m = month + 1; if (m > 11) { setMonth(0); setYear(year + 1); } else setMonth(m); };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };
  const todayIso = ymd(today);

  // ── Timeline (resource) data ──
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const monthStart = ymd(new Date(Date.UTC(year, month, 1)));
  const monthEnd = ymd(new Date(Date.UTC(year, month, daysInMonth)));
  const dayNum = (iso: string) => Number(iso.slice(8, 10));
  const isWeekend = (d: number) => { const wd = (new Date(Date.UTC(year, month, d)).getUTCDay() + 6) % 7; return wd >= 5; };
  const timelineRows = useMemo(() => {
    const byUser = new Map<number, any[]>();
    filtered.forEach(a => {
      if (a.endDate < monthStart || a.startDate > monthEnd) return; // вне месяца
      if (!byUser.has(a.userId)) byUser.set(a.userId, []);
      byUser.get(a.userId)!.push(a);
    });
    return Array.from(byUser.entries())
      .map(([userId, items]) => ({ userId, items: items.sort((x, y) => x.startDate.localeCompare(y.startDate)) }))
      .sort((a, b) => userName(a.userId).localeCompare(userName(b.userId)));
  }, [filtered, monthStart, monthEnd]); // eslint-disable-line

  return (
    <div className="ts-card" style={{ padding: 'var(--space-l2)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', flexWrap: 'wrap', marginBottom: 'var(--space-l2)' }}>
        <div className="section-label" style={{ margin: 0 }}>
          <span className="tr-section-dot" style={{ background: '#7c3aed' }} />{t('HrCalendar')}
        </div>
        <button type="button" className="dt-btn-ghost" onClick={prev}>‹</button>
        <span style={{ fontWeight: 800, fontSize: 14, minWidth: 150, textAlign: 'center', color: 'var(--text-color)' }}>{monthName}</span>
        <button type="button" className="dt-btn-ghost" onClick={next}>›</button>
        <button type="button" className="dt-btn-ghost" onClick={goToday} title={t('HrThisMonth') as string} aria-label={t('HrThisMonth') as string}>
          <FontAwesomeIcon icon={faHouse} />
        </button>
        <div style={{ flex: 1 }} />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">{t('HrAbsenceAll')}</option>
          <option value="leave">{t('HrLeave')}</option>
          <option value="sickness">{t('HrSickness')}</option>
          <option value="lateness">{t('HrLateness')}</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">{t('HrAbsenceAll')}</option>
          <option value="approved">{t('HrStatus_approved')}</option>
          <option value="pending">{t('HrStatus_pending')}</option>
          <option value="recorded">{t('HrStatus_recorded')}</option>
        </select>
        <div className="hr-cal-view">
          <button type="button" className={view === 'month' ? 'active' : ''} title={t('HrViewMonth') as string} onClick={() => setView('month')}>
            <FontAwesomeIcon icon={faTableCellsLarge} />
          </button>
          <button type="button" className={view === 'timeline' ? 'active' : ''} title={t('HrViewTimeline') as string} onClick={() => setView('timeline')}>
            <FontAwesomeIcon icon={faBarsStaggered} />
          </button>
        </div>
      </div>

      {view === 'month' && <div className="hr-cal-grid">
        {WD.map(w => (
          <div key={w} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', textAlign: 'center', padding: 4 }}>
            {t('WD_' + w)}
          </div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={i} className="hr-cal-cell is-out" style={{ border: 'none', background: 'transparent', boxShadow: 'none' }} />;
          const evs = eventsFor(iso);
          const hol = holidayFor(iso);
          const isToday = iso === todayIso;
          const flip = Math.floor(i / 7) >= cells.length / 7 - 2; // нижние ряды — поповер вверх
          const cls = ['hr-cal-cell'];
          if (hol) cls.push('is-hol');
          if (isToday) cls.push('is-today');
          const pinned = pinnedDay === iso;
          return (
            <div key={i} className={cls.join(' ')}
              style={evs.length ? { cursor: 'pointer' } : undefined}
              onClick={evs.length ? (e) => { e.stopPropagation(); setPinnedDay(p => p === iso ? null : iso); } : undefined}>
              <div className="hr-cal-daynum">{Number(iso.slice(8, 10))}</div>
              {hol && <div className="hr-cal-holname">{hol.name}</div>}
              <div className="hr-cal-pile">
                {evs.slice(0, 6).map(a => {
                  const color = userColor(a.userId);
                  const pending = a.status === 'pending';
                  return (
                    <span key={a.id} className={`hr-cal-av${pending ? ' is-pending' : ''}`}
                      title={`${userName(a.userId)} · ${a.category}${pending ? ' · pending' : ''}`}
                      style={{ color: pending ? color : '#fff', background: pending ? undefined : color }}>
                      {initials(a.userId)}
                    </span>
                  );
                })}
                {evs.length > 6 && <span className="hr-cal-more">+{evs.length - 6}</span>}
              </div>

              {evs.length > 0 && (
                <div className={`hr-cal-pop${flip ? ' flip' : ''}${pinned ? ' is-open' : ''}`}
                  onClick={(e) => e.stopPropagation()}>
                  <div className="hr-cal-pop-h">
                    <span className="hr-cal-pop-date">{Number(iso.slice(8, 10))} {monthShort}</span>
                    <span className="hr-cal-pop-count">{evs.length} {t('HrOut')}</span>
                    <button type="button" className="hr-cal-pop-close" title={t('Cancel') as string} onClick={() => setPinnedDay(null)}>✕</button>
                  </div>
                  <div className="hr-cal-pop-list">
                    {evs.map(a => {
                      const pending = a.status === 'pending';
                      const tc = typeColor(a.typeId);
                      const role = userRole(a.userId);
                      return (
                        <div key={a.id} className="hr-cal-pop-row">
                          <span className="hr-cal-pop-av" style={{ background: userColor(a.userId) }}>{initials(a.userId)}</span>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <div className="hr-cal-pop-nm">{userName(a.userId)}</div>
                            <div className="hr-cal-pop-sub">
                              {role ? role + ' · ' : ''}
                              {formatDate(a.startDate)}{a.endDate !== a.startDate ? ` → ${formatDate(a.endDate)}` : ''}
                            </div>
                            <span className={`sbadge ${a.status === 'approved' ? 'fulfilled' : a.status === 'rejected' ? 'rejected' : a.status === 'pending' ? 'pending' : 'new'}`} style={{ fontSize: 10 }}>
                              {t('HrStatus_' + a.status)}
                            </span>
                          </div>
                          <span className="sbadge hr-cal-pop-chip" style={{ background: `${tc}22`, color: tc }}>{typeName(a.typeId)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>}

      {view === 'timeline' && (
        <div className="hr-tl-wrap">
          <table className="hr-tl">
            <thead>
              <tr>
                <th className="hr-tl-name">{t('Name')}</th>
                {Array.from({ length: daysInMonth }, (_, k) => k + 1).map(d => (
                  <th key={d} className={`${isWeekend(d) ? 'is-we' : ''} ${ymd(new Date(Date.UTC(year, month, d))) === todayIso ? 'is-today' : ''}`}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timelineRows.length === 0 ? (
                <tr><td className="hr-tl-name">—</td><td colSpan={daysInMonth} style={{ color: 'var(--text-color-calendar-week)', fontSize: 12 }}>{t('Empty')}</td></tr>
              ) : timelineRows.map(row => {
                const color = userColor(row.userId);
                const cellsArr: React.ReactNode[] = [];
                let d = 1;
                while (d <= daysInMonth) {
                  const iso = ymd(new Date(Date.UTC(year, month, d)));
                  const a = row.items.find((x: any) => {
                    const vs = x.startDate < monthStart ? monthStart : x.startDate;
                    return vs === iso;
                  });
                  if (a) {
                    const vs = a.startDate < monthStart ? monthStart : a.startDate;
                    const ve = a.endDate > monthEnd ? monthEnd : a.endDate;
                    const span = dayNum(ve) - dayNum(vs) + 1;
                    const pending = a.status === 'pending';
                    cellsArr.push(
                      <td key={d} className="hr-tl-barcell" colSpan={span}>
                        <div className={`hr-tl-bar${pending ? ' is-pending' : ''}`} title={`${userName(a.userId)} · ${a.category}`}
                          style={{ color: pending ? color : '#fff', background: pending ? undefined : color }}>
                          {typeName(a.typeId)}
                        </div>
                      </td>,
                    );
                    d += span;
                  } else {
                    cellsArr.push(<td key={d} className={isWeekend(d) ? 'is-we' : ''} />);
                    d += 1;
                  }
                }
                const u = users.find(x => x.id === row.userId);
                return (
                  <tr key={row.userId}>
                    <td className="hr-tl-name">
                      <span className="hr-tl-ava" style={{ background: color }}>{initials(row.userId)}</span>
                      {u ? `${u.first_name} ${u.last_name}` : `#${row.userId}`}
                    </td>
                    {cellsArr}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HrAbsenceCalendar;
